import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { Speaker } from './SpeakerTypes';

interface SingleSpeakerSelectProps {
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  onChange: (selected: Speaker[]) => void;
  placeholder?: string;
}

export const SingleSpeakerSelect: React.FC<SingleSpeakerSelectProps> = ({
  speakers,
  selectedSpeakers,
  onChange,
  placeholder = 'Select a speaker',
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const currentSpeaker = selectedSpeakers[0];
  const currentLabel = currentSpeaker?.speakerLabel;

  const displayText = currentSpeaker
    ? currentSpeaker.isAnonymous
      ? `Speaker ${currentSpeaker.speakerLabel.replace('spk_', '')}`
      : currentSpeaker.name || currentSpeaker.speakerLabel
    : '';

  const close = useCallback(() => {
    setOpen(false);
    setHighlight(null);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!open) return;
      const target = e.target;
      if (!(target instanceof Node)) return;
      const triggerEl = triggerRef.current;
      const listEl = listRef.current;
      if (
        triggerEl &&
        !triggerEl.contains(target) &&
        listEl &&
        !listEl.contains(target)
      ) {
        close();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, close]);

  const selectSpeaker = (speakerLabel: string) => {
    const sp = speakers.find(s => s.speakerLabel === speakerLabel);
    if (sp) onChange([sp]);
    else onChange([]);
    close();
  };

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else if (highlight !== null) {
        selectSpeaker(speakers[highlight].speakerLabel);
      }
    } else if (e.key === 'Escape') {
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight(prev => {
        const next =
          prev === null ? 0 : Math.min(prev + 1, speakers.length - 1);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight(prev => {
        const next = prev === null ? 0 : Math.max(prev - 1, 0);
        return next;
      });
    }
  };

  useEffect(() => {
    if (open && highlight !== null) {
      const listEl = listRef.current;
      if (!listEl) return;
      const child = listEl.children.item(highlight);
      if (child && child instanceof HTMLElement) {
        const top = child.offsetTop;
        const bottom = top + child.offsetHeight;
        if (top < listEl.scrollTop) listEl.scrollTop = top;
        else if (bottom > listEl.scrollTop + listEl.clientHeight) {
          listEl.scrollTop = bottom - listEl.clientHeight;
        }
      }
    }
  }, [open, highlight]);

  return (
    <SingleRoot>
      <SingleTrigger
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleKey}
        $isOpen={open}
        $hasValue={!!displayText}
      >
        <span className="label">
          {displayText || <span className="placeholder">{placeholder}</span>}
        </span>
        <SingleArrow $open={open}>
          <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
            <path d="M2 4.33008L6 8.33008L10 4.33008" stroke="#F2D093" />
          </svg>
        </SingleArrow>
      </SingleTrigger>
      {open && (
        <SingleDropdown ref={listRef} role="listbox">
          {speakers.map((s, idx) => {
            const text = s.isAnonymous
              ? `Speaker ${s.speakerLabel.replace('spk_', '')}`
              : s.name || s.speakerLabel;
            const active = highlight === idx;
            const selected = currentLabel === s.speakerLabel;
            return (
              <SingleItem
                key={s.speakerLabel}
                $active={active}
                $selected={selected}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => selectSpeaker(s.speakerLabel)}
              >
                {text}
              </SingleItem>
            );
          })}
        </SingleDropdown>
      )}
    </SingleRoot>
  );
};

// Styled components (extracted from previous combined file)
const SingleRoot = styled.div`
  position: relative;
  margin-top: 12px;
  font-size: 14px;
`;

const SingleTrigger = styled.div<{ $isOpen: boolean; $hasValue: boolean }>`
  border: 1px solid ${p => (p.$isOpen ? '#f2d093' : '#484848')};
  background: #03041a;
  color: #f3e9d7;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition:
    border-color 0.18s ease,
    background 0.18s ease;

  &:hover {
    border-color: #5a5a5a;
  }
  .placeholder {
    color: #484848;
  }
`;

const SingleArrow = styled.span<{ $open: boolean }>`
  display: flex;
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`;

const SingleDropdown = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #484848;
  border-radius: 8px;
  background-color: #03041a;
  box-shadow: 8px 16px 8px 0px rgba(0, 0, 0, 0.4);
  padding: 4px 6px;
  max-height: 200px;
  width: 100%;
  overflow: auto;
  position: absolute;
  top: 46px;
  left: 0;
  z-index: 20;
  scrollbar-color: #35393a transparent;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

const SingleItem = styled.button<{ $active: boolean; $selected: boolean }>`
  padding: 6px 12px;
  text-align: left;
  background: transparent;
  border: 0;
  outline: 0;
  color: ${p => (p.$selected ? '#f2d093' : '#f3e9d7')};
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  ${p => p.$active && 'background: rgba(255,255,255,0.05);'}
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;
